/**
 * Aged Care Schedule – single source of truth for means-tested fee rates and thresholds.
 * Aligned with Aged Care Act 2024 s319 (daily means tested amount), s278 (hotelling), s279 (NCCC).
 * Ported verbatim from frontend/src/constants/agedCareSchedule.js.
 *
 * SOURCE: the Department of Health, Disability and Ageing's "Schedule of fees and charges for
 * residential care". It is published THREE times a year, not two: 20 March, 1 JULY and 20 September.
 * The 1 July edition is easy to miss because the Age Pension does not move then — but on 1 July 2026
 * it moved the means-test income thresholds, the fourth asset threshold and the maximum RAD, and this
 * file sat on the March figures for ten weeks. On a client with $544,884 of assessable assets that
 * showed a $1.82/day NCCC the Act did not ask for (the fourth asset threshold had moved past them).
 * `agedCareScheduleOfficial.test.ts` now pins every figure here to the current official document, so
 * the next edition has to be a deliberate update of both.
 */

export const AGED_CARE_SCHEDULE = {
    /** Date of the DoH schedule these figures are taken from (editions: 20 Mar, 1 Jul, 20 Sep); MPIR updates quarterly separately. */
    effectiveDate: '2026-09-20',

    /** Maximum Permissible Interest Rate — Department of Health, quarterly. */
    mpiQuarterLabel: '1 October 2026 – 31 December 2026',

    // --- Income thresholds (annual) – Single --- (Schedule 20 Sep 2026)
    incomeFreeAreaSingle: 36454.60,
    firstIncomeThresholdSingle: 90130.04,
    secondIncomeThresholdSingle: 105048,
    thirdIncomeThresholdSingle: 121173.20,
    fourthIncomeThresholdSingle: 145818.40,

    // --- Income thresholds (annual) – Couple (illness separated, single rate) --- (Schedule 20 Sep 2026)
    incomeFreeAreaCouple: 35726.60,
    firstIncomeThresholdCouple: 89402.04,
    secondIncomeThresholdCouple: 105048,
    thirdIncomeThresholdCouple: 121173.20,
    fourthIncomeThresholdCouple: 142906.40,

    // --- Asset thresholds (same for single and couple) --- (Schedule 20 Sep 2026)
    assetFreeArea: 66500,
    firstAssetThreshold: 219858.40,
    secondAssetThreshold: 266000,
    thirdAssetThreshold: 369366.66,
    fourthAssetThreshold: 552858.40,

    // --- Caps and supplements (daily unless noted) --- (Schedule 20 Sep 2026)
    MAS: 73.73,
    maxHotellingDaily: 22.15,
    maxNCCCDaily: 109.45,
    ncccLifetimeCap: 140652.80, // also a 4-year cap applies (nccc4YearDays). Indexed 20 Mar & 20 Sep.
    /** The Department's own figure — "1,460 days (not indexed)" — rather than 4 × 365 + a leap day. */
    nccc4YearDays: 1460,

    // --- Other --- (Schedule 20 Sep 2026)
    homeExemptionCap: 219858.40,
    /** Max daily BDF (Schedule 20 Sep 2026, 1 Nov 2025 arrangements) = 85% of basic single age pension ($1,135.40/fn). */
    basicDailyFeeDaily: 68.93,
    basicDailyFeeRate: 0.85,
    singlePensionAnnual: 32180.20,
    /** 1 October – 31 December 2026 (applied 2 October 2026 from RATES_FROM_1_OCTOBER_2026; was 8.43%). */
    MPIR: 0.0851,
    /** Max refundable accommodation deposit without IHACPA approval (Schedule 20 Sep 2026 — unchanged). */
    maxRAD: 789686,
    /** Minimum permissible asset level (Schedule 20 Sep 2026). */
    minPermissibleAssetLevel: 66500,

    /**
     * THE 1 JULY 2014 FEE ARRANGEMENTS — the means-tested care fee, kept open for residents covered
     * by the "no worse off" principle (on 12 September 2024 receiving, approved for or waiting for a
     * Home Care Package; entering residential care from 1 November 2025; not opted in). Same
     * schedule edition, section "Rates for post 1 July 2014 fee and accommodation arrangements";
     * the formula is the Department's residential fee scenarios for the 1 July 2014 arrangements
     * (Sofia / Jane / Roger). Verified 17 Sep 2026 against the 1 July 2026 schedule (v2.0); figures
     * moved to the 20 September 2026 schedule on 21 Sep 2026.
     *
     *   income-tested amount = 50% × (assessable income − income free area) ÷ 364
     *   asset-tested amount  = [17.5% × (assets between the asset free area and the first threshold)
     *                          + 1% × (between the first and second thresholds)
     *                          + 2% × (above the second threshold)] ÷ 364
     *   means-tested care fee = min( max(0, MTA − maximum accommodation supplement), daily maximum )
     *
     * The asset free area, first threshold, home exemption cap and maximum accommodation supplement
     * are the same figures as the 1 November 2025 rules use; the second threshold is not.
     */
    arrangements2014: {
        incomeFreeAreaSingle: 36454.60,
        /** Couple, illness separated — the single rate row. */
        incomeFreeAreaCouple: 35726.60,
        incomeRate: 0.5,
        assetFreeArea: 66500,
        firstAssetThreshold: 219858.40,
        secondAssetThreshold: 526575.20,
        assetRateBand1: 0.175,
        assetRateBand2: 0.01,
        assetRateBand3: 0.02,
        /** "Means tested care fee — from 1 October 2026" maximum daily rate (applied 2 October 2026; was $372.03). */
        maxMeansTestedCareFeeDaily: 380.46,
        /** Fee stops for the rest of the year of care once reached; restarts on the anniversary of entry. */
        mtcfAnnualCap: 37055.98,
        /** Shared with Home Care Package income-tested care fees and Support at Home no-worse-off contributions. */
        mtcfLifetimeCap: 88934.57,
    },
};

/**
 * The schedule as it stands `factor` × later — every DOLLAR threshold, cap and supplement scaled
 * together, the rates and the day counts left alone. The projection uses this to reassess a
 * resident's means-tested fees in each later year against thresholds that have moved with prices,
 * the way it already indexes the pension's limits (financialCalculations.getIndexedRates). The real
 * thresholds index on 20 March and 20 September by their own factors; one CPI assumption is the
 * projection's standing approximation for all of them.
 */
export function indexedAgedCareSchedule(factor: number): typeof AGED_CARE_SCHEDULE {
    const s = AGED_CARE_SCHEDULE;
    const x = (v: number) => v * factor;
    return {
        ...s,
        incomeFreeAreaSingle: x(s.incomeFreeAreaSingle),
        firstIncomeThresholdSingle: x(s.firstIncomeThresholdSingle),
        secondIncomeThresholdSingle: x(s.secondIncomeThresholdSingle),
        thirdIncomeThresholdSingle: x(s.thirdIncomeThresholdSingle),
        fourthIncomeThresholdSingle: x(s.fourthIncomeThresholdSingle),
        incomeFreeAreaCouple: x(s.incomeFreeAreaCouple),
        firstIncomeThresholdCouple: x(s.firstIncomeThresholdCouple),
        secondIncomeThresholdCouple: x(s.secondIncomeThresholdCouple),
        thirdIncomeThresholdCouple: x(s.thirdIncomeThresholdCouple),
        fourthIncomeThresholdCouple: x(s.fourthIncomeThresholdCouple),
        assetFreeArea: x(s.assetFreeArea),
        firstAssetThreshold: x(s.firstAssetThreshold),
        secondAssetThreshold: x(s.secondAssetThreshold),
        thirdAssetThreshold: x(s.thirdAssetThreshold),
        fourthAssetThreshold: x(s.fourthAssetThreshold),
        MAS: x(s.MAS),
        maxHotellingDaily: x(s.maxHotellingDaily),
        maxNCCCDaily: x(s.maxNCCCDaily),
        ncccLifetimeCap: x(s.ncccLifetimeCap),
        homeExemptionCap: x(s.homeExemptionCap),
        basicDailyFeeDaily: x(s.basicDailyFeeDaily),
        singlePensionAnnual: x(s.singlePensionAnnual),
        maxRAD: x(s.maxRAD),
        minPermissibleAssetLevel: x(s.minPermissibleAssetLevel),
        arrangements2014: {
            ...s.arrangements2014,
            incomeFreeAreaSingle: x(s.arrangements2014.incomeFreeAreaSingle),
            incomeFreeAreaCouple: x(s.arrangements2014.incomeFreeAreaCouple),
            assetFreeArea: x(s.arrangements2014.assetFreeArea),
            firstAssetThreshold: x(s.arrangements2014.firstAssetThreshold),
            secondAssetThreshold: x(s.arrangements2014.secondAssetThreshold),
            maxMeansTestedCareFeeDaily: x(s.arrangements2014.maxMeansTestedCareFeeDaily),
            mtcfAnnualCap: x(s.arrangements2014.mtcfAnnualCap),
            mtcfLifetimeCap: x(s.arrangements2014.mtcfLifetimeCap),
        },
    };
}

export default AGED_CARE_SCHEDULE;
